import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { UpdateSubStepApprovalNoteRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import { formatDateTimeFull } from '~/models/dateTime'
import { MemoDialog } from '~/pages/WorkflowCaseDetail/components/MemoDialog'
import { useUpdateSubStepApprovalNote } from '~/pages/WorkflowCaseDetail/hooks/useUpdateSubStepApprovalNote'

type Props = {
  isOpen: boolean
  caseId: string
  bigStepDisplayOrder: number
  subStepDisplayOrder: number
  stageOrder: number
  approverMemberId: string
  initialNote: string | undefined
  approverName: string
  approvedAt: Timestamp | undefined
  onClose: () => void
}

export const ApprovalMemoDialog = ({
  isOpen,
  caseId,
  bigStepDisplayOrder,
  subStepDisplayOrder,
  stageOrder,
  approverMemberId,
  initialNote,
  approverName,
  approvedAt,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useUpdateSubStepApprovalNote(caseId)

  const handleSave = async (memo: string): Promise<void> => {
    await mutateAsync(
      create(UpdateSubStepApprovalNoteRequestSchema, {
        workflowCaseId: caseId,
        bigStepDisplayOrder,
        subStepDisplayOrder,
        stageOrder,
        approverMemberId,
        note: memo === '' ? undefined : memo,
      }),
    )
    // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
    showSuccessToast({
      title: t('page:workflowCaseDetail.approvalToast.memoSaveToast'),
    })
  }

  const mapError = (err: unknown): string | undefined => {
    if (err instanceof ConnectError) {
      if (err.code === Code.Aborted) {
        return t('page:workflowCaseDetail.error.aborted')
      }
      if (err.code === Code.FailedPrecondition) {
        return t('page:workflowCaseDetail.error.failedPrecondition')
      }
    }
    return undefined
  }

  return (
    <MemoDialog
      context={
        <VStack align="stretch" gap={1}>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:workflowCaseDetail.approval.memoDialogApproverLabel')}
          </Text>
          <Box>
            <Text color="Text.highEmphasis" fontSize="sm">
              {approverName}
            </Text>
          </Box>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:workflowCaseDetail.approval.memoDialogAtLabel')}
          </Text>
          <Text color="Text.midEmphasis" fontSize="sm">
            {approvedAt !== undefined
              ? formatDateTimeFull(timestampDate(approvedAt))
              : '-'}
          </Text>
        </VStack>
      }
      initialMemo={initialNote}
      isOpen={isOpen}
      isPending={isPending}
      mapError={mapError}
      onClose={onClose}
      onSave={handleSave}
      placeholder={t('page:workflowCaseDetail.approval.memoDialogPlaceholder')}
      saveLabel={t('page:workflowCaseDetail.approval.memoDialogSave')}
      textareaLabel={t(
        'page:workflowCaseDetail.approval.memoDialogTextareaLabel',
      )}
      title={t('page:workflowCaseDetail.approval.memoDialogTitle')}
    />
  )
}
